import { useState, useEffect, useCallback } from 'react';
import { api } from '@/services/api-class';
import type { FormSchemaData } from '@/services/api-types';

export interface UseFormSchemasOptions {
  /**
   * e.g. 'CLIENT' or 'CASE_REPORT'. Pass `null` to list every host type
   * (the list endpoints treat attachTo as optional). Use `autoLoad: false`
   * to hold the request until the caller is ready.
   */
  attachTo: string | null;
  /** Whether to auto-load. Default `true`. */
  autoLoad?: boolean;
  /**
   * 'authoring' (default) hits the admin CRUD list — requires
   * ROLE_FORM_MANAGEMENT; used by the Custom Forms screen.
   * 'display' hits the module-scoped read list — any module user; active
   * schemas only. Used by screens that render forms rather than manage them.
   */
  scope?: 'authoring' | 'display';
}

export interface UseFormSchemasResult {
  schemas: FormSchemaData[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetches the list of form schemas attached to a given host record type, or
 * every schema when `attachTo` is null.
 * Recomputes when `attachTo` changes. Wraps the callback-style
 * `api.getFormSchemas` — see docs/house-check-request/implementation-plan.md §2.5.
 */
export function useFormSchemas({
  attachTo,
  autoLoad = true,
  scope = 'authoring',
}: UseFormSchemasOptions): UseFormSchemasResult {
  const [schemas, setSchemas] = useState<FormSchemaData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const fetchSchemas = scope === 'display'
      ? api.getFormSchemasForDisplay.bind(api)
      : api.getFormSchemas.bind(api);
    fetchSchemas(
      attachTo,
      (result: FormSchemaData[] | null | undefined) => {
        setSchemas(Array.isArray(result) ? result : []);
        setIsLoading(false);
      },
      (err: any) => {
        setError(err?.message ?? 'Failed to load form schemas');
        setSchemas([]);
        setIsLoading(false);
      },
    );
  }, [attachTo, scope]);

  useEffect(() => {
    if (!autoLoad) return;
    load();
  }, [autoLoad, load]);

  return {
    schemas,
    isLoading,
    error,
    reload: load,
  };
}
